import React, { useEffect, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import axios from "axios";

// Ícone padrão Leaflet
import iconUrl from "leaflet/dist/images/marker-icon.png";
import shadowUrl from "leaflet/dist/images/marker-shadow.png";
const defaultIcon = L.icon({
    iconUrl,
    shadowUrl,
    iconSize: [25, 41],
    iconAnchor: [12, 41],
});
L.Marker.prototype.options.icon = defaultIcon;

const BASE_URL = process.env.REACT_APP_API_URL;

// Tipos
type Props = {
    pontosImportantes?: string[];
    pontosInteresse?: string[];
};

type Localizacao = {
    nome: string;
    lat: number;
    lon: number;
    tipo: "importante" | "interesse";
};

// Ajusta bounds automaticamente
const FitBounds: React.FC<{ locations: Localizacao[] }> = ({ locations }) => {
    const map = useMap();

    useEffect(() => {
        if (locations.length === 0) return;
        const bounds = L.latLngBounds(locations.map((loc) => [loc.lat, loc.lon]));
        map.fitBounds(bounds, { padding: [50, 50] });
    }, [locations, map]);

    return null;
};

// Função utilitária para buscar coordenadas no backend
const buscarCoordenada = async (
    nome: string
): Promise<{ lat: number; lon: number } | null> => {
    try {
        const res = await axios.get(`${BASE_URL}/nominatim/search`, {
            params: { q: nome },
        });
        const data = res.data;
        if (Array.isArray(data) && data.length > 0) {
            return { lat: parseFloat(data[0].lat), lon: parseFloat(data[0].lon) };
        }
        return null;
    } catch (error) {
        console.error("Erro ao buscar coordenada de", nome, error);
        return null;
    }
};

// Ícones customizados
const importantIcon = L.icon({
    iconUrl: "/assets/img/location/location-red.png",
    iconSize: [40, 50],
    iconAnchor: [15, 50],
});

const interestIcon = L.icon({
    iconUrl: "/assets/img/location/location-blue.png",
    iconSize: [40, 50],
    iconAnchor: [15, 50],
});

const MapaPacote: React.FC<Props> = ({
    pontosImportantes = [],
    pontosInteresse = [],
}) => {
    const [localizacoes, setLocalizacoes] = useState<Localizacao[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const carregar = async () => {
            const locais: Localizacao[] = [];

            // Busca coordenadas para pontos importantes
            for (const ponto of pontosImportantes) {
                const coords = await buscarCoordenada(ponto);
                if (coords) {
                    locais.push({
                        nome: ponto,
                        lat: coords.lat,
                        lon: coords.lon,
                        tipo: "importante",
                    });
                }
            }

            // Busca coordenadas para pontos de interesse
            for (const ponto of pontosInteresse) {
                const coords = await buscarCoordenada(ponto);
                if (coords) {
                    locais.push({
                        nome: ponto,
                        lat: coords.lat,
                        lon: coords.lon,
                        tipo: "interesse",
                    });
                }
            }

            setLocalizacoes(locais);
            setLoading(false);
        };

        carregar();
    }, [pontosImportantes, pontosInteresse]);

    if (loading) return <p>Loading map...</p>;
    if (localizacoes.length === 0) return <p>No points found.</p>;

    const center: [number, number] = [38.716946, -9.142685]; // fallback

    return (
        <div style={{ width: "100%", height: "100%", position: "relative" }}>
            <MapContainer center={center} zoom={6} style={{ width: "100%", height: "100%" }}>
                <TileLayer
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    attribution='&copy; <a href="https://osm.org">OpenStreetMap</a> contributors'
                />

                {localizacoes.map((loc, idx) => (
                    <Marker
                        key={idx}
                        position={[loc.lat, loc.lon]}
                        icon={loc.tipo === "importante" ? importantIcon : interestIcon}
                    >
                        <Popup>{loc.nome}</Popup>
                    </Marker>
                ))}

                <FitBounds locations={localizacoes} />
            </MapContainer>

            {/* Legenda */}
            <div
                style={{
                    position: "absolute",
                    top: 10,
                    right: 10,
                    backgroundColor: "white",
                    padding: "8px 12px",
                    borderRadius: "6px",
                    boxShadow: "0 2px 6px rgba(0,0,0,0.3)",
                    fontSize: "14px",
                    zIndex: 1000,
                }}
            >
                <div style={{ display: "flex", alignItems: "center", marginBottom: 4 }}>
                    <img src="/assets/img/location/location-red.png" alt="Important" style={{ width: 30, height: 33, marginRight: 6 }} />
                    <span>Important Point: Part of the tour itself</span>
                </div>
                <div style={{ display: "flex", alignItems: "center" }}>
                    <img src="/assets/img/location/location-blue.png" alt="Interest" style={{ width: 30, height: 33, marginRight: 6 }} />
                    <span>Point of Interest: Not part of the tour but worth visiting</span>
                </div>
            </div>
        </div>
    );
};

export default MapaPacote;